/**
 * Public API client surface.
 *
 * App code imports from `@/lib/api` — never from individual resource files in
 * application boundary code. This indirection lets us swap implementations
 * (real ↔ mock ↔ hybrid) without touching call sites.
 *
 * Mode (`NEXT_PUBLIC_API_MODE`):
 *   real    — fetches go to NEXT_PUBLIC_API_BASE_URL.
 *   mock    — every request is intercepted by MSW (no backend needed).
 *   hybrid  — real for backend-existing endpoints; MSW intercepts the
 *             assignment-layer surface (which the backend doesn't have yet).
 *
 * The MODE flag is consumed by `lib/mock/start.ts` (P0-B.2) to decide which
 * handlers to register — the client itself just talks fetch.
 */

export * from './client';
export * from './errors';

export * as brokers from './brokers';
export * as strategies from './strategies';
export * as subscriptions from './subscriptions';

// Assignment-layer surface — MSW-backed until backend ships these endpoints.

/** Returns the current API mode for diagnostics surfaces. */
export type ApiMode = 'real' | 'mock' | 'hybrid';

export const getApiMode = (): ApiMode => {
  const v = process.env.NEXT_PUBLIC_API_MODE;
  if (v === 'mock' || v === 'hybrid') {
    return v;
  }
  // FAIL CLOSED: this console fronts a live real-money engine. Unset or
  // unrecognised mode means REAL backend + real auth — mock data requires
  // an explicit NEXT_PUBLIC_API_MODE=mock|hybrid opt-in.
  return 'real';
};

